// @tennis-matches/services/trainerstunden/billing-session-lock
// Phase 1: Erkennung, ob ein Training wegen verschickter/teilbezahlter/bezahlter Rechnungen nicht mehr still geändert werden darf.

import { normalizePid } from "./billing-ledger-share.js";
import { isCancelledSessionStatus } from "./session-status.js";

/** @readonly */
export const BILLING_SESSION_LOCKED_ERROR = "billing-session-locked";

/**
 * Statement-Status, die eine direkte Änderung der Ursprungs-Session/Leistung auslösen
 * (alle Empfänger/Anteile derselben Session).
 * Entwurf = bereits im Rechnungslauf erfasst; Storno (`cancelled`) sperrt nicht.
 * @readonly
 */
export const BILLING_LOCK_BLOCKING_STATEMENT_STATUSES = Object.freeze([
  "draft",
  "sent",
  "partially_paid",
  "paid",
]);

/** @readonly */
export const BILLING_LOCK_SENSITIVE_SESSION_KEYS = Object.freeze(
  new Set([
    "planned_ids",
    "present_ids",
    "attendance_confirmed",
    "date",
    "start_time",
    "duration_min",
    "duration_minutes",
    "pricing_type",
    "rate_eur_per_hour",
    "fixed_total_eur",
    "split_base",
    "status",
    "cancelled_reason",
    "cancelled_note",
    "cancelled_at",
    "cancelled_by",
  ]),
);

/** Statement-Status, ab dem eine Rechnung als „verschickt“ gilt (Korrektur anderer Spieler). */
export const BILLING_SENT_STATEMENT_STATUSES = Object.freeze([
  "sent",
  "partially_paid",
  "paid",
]);

const BLOCKING = new Set(BILLING_LOCK_BLOCKING_STATEMENT_STATUSES);
const SENT = new Set(BILLING_SENT_STATEMENT_STATUSES);

/**
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
export function sessionUpdatesRequireBillingLockCheck(updates) {
  if (!updates || typeof updates !== "object") return false;
  return Object.keys(updates).some((k) =>
    BILLING_LOCK_SENSITIVE_SESSION_KEYS.has(k),
  );
}

/** @readonly */
const ATTENDANCE_UNSET_ALLOWED_KEYS = Object.freeze(
  new Set([
    "attendance_confirmed",
    "status",
    "present_despite_decline_ids",
    "cancelled_reason",
    "cancelled_note",
    "cancelled_at",
    "cancelled_by",
  ]),
);

/**
 * Bestätigung zurücksetzen oder als Absage markieren (ohne Planungs-/Preisfelder).
 *
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
export function sessionUpdatesAreAttendanceUnsetOnly(updates) {
  if (!updates || typeof updates !== "object") return false;
  if (updates.attendance_confirmed !== false) return false;
  const keys = Object.keys(updates);
  return (
    keys.length > 0 && keys.every((k) => ATTENDANCE_UNSET_ALLOWED_KEYS.has(k))
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
export function sessionUpdatesAreAttendanceResetOnly(updates) {
  if (!sessionUpdatesAreAttendanceUnsetOnly(updates)) return false;
  const keys = Object.keys(updates);
  const allowed = new Set([
    "attendance_confirmed",
    "status",
    "present_despite_decline_ids",
  ]);
  return keys.every((k) => allowed.has(k));
}

/**
 * Erkennt eine echte Absage (nicht nur Bestätigungs-Reset): Session wird auf einen
 * cancelled-Status gesetzt und die Anwesenheit zurückgenommen.
 * Absagen dürfen auch bei bereits versendeten Rechnungen erfolgen (Rechnung bleibt
 * unverändert, optional Gutschrift), daher separater Guard-Pfad.
 *
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
const CANCELLATION_ALLOWED_KEYS = Object.freeze(
  new Set([
    "attendance_confirmed",
    "status",
    "present_ids",
    "present_despite_decline_ids",
    "cancelled_reason",
    "cancelled_note",
    "cancelled_at",
    "cancelled_by",
    "makeup_waived_at",
    "honorar_payable_without_invoice",
  ]),
);

export function sessionUpdatesAreCancellation(updates) {
  if (!updates || typeof updates !== "object") return false;
  if (updates.attendance_confirmed !== false) return false;
  const hasCancelMarker =
    ("cancelled_at" in updates &&
      String(updates.cancelled_at ?? "").trim() !== "") ||
    ("cancelled_reason" in updates &&
      String(updates.cancelled_reason ?? "").trim() !== "") ||
    isCancelledSessionStatus(updates.status);
  if (!hasCancelMarker) return false;
  const keys = Object.keys(updates);
  return keys.length > 0 && keys.every((k) => CANCELLATION_ALLOWED_KEYS.has(k));
}

/** @readonly */
const PARENT_BILLING_ZERO_ALLOWED_KEYS = Object.freeze(
  new Set([
    "rate_eur_per_hour",
    "fixed_total_eur",
    "honorar_payable_without_invoice",
  ]),
);

/**
 * Eltern-Rechnung dauerhaft auf 0 € (Training bleibt bestätigt) — z. B. beim
 * Löschen eines Rechnungsentwurfs. Nur Entwürfe sperren nicht; versendete
 * Rechnungen schon.
 *
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
export function sessionUpdatesAreParentBillingZero(updates) {
  if (!updates || typeof updates !== "object") return false;
  const keys = Object.keys(updates);
  if (!keys.length) return false;
  if (!keys.every((k) => PARENT_BILLING_ZERO_ALLOWED_KEYS.has(k))) return false;
  if (!("rate_eur_per_hour" in updates) && !("fixed_total_eur" in updates)) {
    return false;
  }
  const rateOk =
    !("rate_eur_per_hour" in updates) ||
    !(Number(updates.rate_eur_per_hour) > 0.004);
  const fixedOk =
    !("fixed_total_eur" in updates) ||
    !(Number(updates.fixed_total_eur) > 0.004);
  return rateOk && fixedOk;
}

/** @readonly */
const ATTENDANCE_CONFIRM_ALLOWED_KEYS = Object.freeze(
  new Set([
    "present_ids",
    "declined_ids",
    "present_despite_decline_ids",
    "planned_ids",
    "attendance_confirmed",
    "status",
    "cancelled_at",
    "cancelled_reason",
    "cancelled_note",
    "cancelled_by",
  ]),
);

/** @readonly */
const ATTENDANCE_PRESENCE_ALLOWED_KEYS = Object.freeze(
  new Set(["present_ids", "declined_ids", "present_despite_decline_ids"]),
);

/**
 * Anwesenheit bestätigen (Trainer-Hub) — Entwürfe auf anderen Teilnehmern sperren nicht.
 *
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
export function sessionUpdatesAreAttendanceConfirmOnly(updates) {
  if (!updates || typeof updates !== "object") return false;
  if (updates.attendance_confirmed !== true) return false;
  const keys = Object.keys(updates);
  return (
    keys.length > 0 && keys.every((k) => ATTENDANCE_CONFIRM_ALLOWED_KEYS.has(k))
  );
}

/**
 * Nur Anwesenheitslisten korrigieren (ohne Bestätigungs-Flag / Status).
 *
 * @param {Record<string, unknown>|null|undefined} updates
 * @returns {boolean}
 */
export function sessionUpdatesAreAttendancePresenceOnly(updates) {
  if (!updates || typeof updates !== "object") return false;
  const keys = Object.keys(updates);
  if (!keys.length) return false;
  if ("attendance_confirmed" in updates || "status" in updates) return false;
  return keys.every((k) => ATTENDANCE_PRESENCE_ALLOWED_KEYS.has(k));
}

function correctionPipelineBlocksSession(sessionId, ctx) {
  const sid = String(sessionId ?? "").trim();
  if (!sid) return { blocked: false, blockingCorrectionIds: [] };

  const safe = ctx && typeof ctx === "object" ? ctx : {};
  const postings = Array.isArray(safe.billingCorrectionPostings)
    ? safe.billingCorrectionPostings
    : [];
  const corrHdr = Array.isArray(safe.billingCorrectionsActiveHeaders)
    ? safe.billingCorrectionsActiveHeaders
    : [];

  const blockingCorrFromPostings = new Set();
  for (const p of postings) {
    if (String(p?.correction_session_id ?? "").trim() !== sid) continue;
    if (String(p?.correction_status ?? "").trim() !== "booked") continue;
    const cid = String(p?.correction_id ?? "").trim();
    if (cid) blockingCorrFromPostings.add(cid);
  }
  if (blockingCorrFromPostings.size > 0) {
    return {
      blocked: true,
      reasonKey:
        "trainerstunden.billing.openItem.performanceBlockedCorrectionBooked",
      blockingCorrectionIds: [...blockingCorrFromPostings],
    };
  }

  const activeHdr = corrHdr.filter((c) => {
    if (String(c?.session_id ?? "").trim() !== sid) return false;
    const st = String(c?.status ?? "").trim();
    return st === "draft" || st === "confirmed" || st === "booked";
  });
  if (activeHdr.length > 0) {
    return {
      blocked: true,
      reasonKey:
        "trainerstunden.billing.openItem.performanceBlockedCorrectionPipeline",
      blockingCorrectionIds: [
        ...new Set(
          activeHdr.map((c) => String(c?.id ?? "").trim()).filter(Boolean),
        ),
      ],
    };
  }

  return { blocked: false, blockingCorrectionIds: [] };
}

/**
 * @param {{
 *   sessionId: string,
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 *   blockingStatuses?: readonly string[],
 * }} args
 * @returns {{
 *   locked: boolean,
 *   affectedStatements: unknown[],
 *   affectedItems: unknown[],
 *   blockingStatuses: string[],
 * }}
 */
export function getBillingLockInfoForSession({
  sessionId,
  billingStatements,
  billingStatementItems,
  blockingStatuses = BILLING_LOCK_BLOCKING_STATEMENT_STATUSES,
}) {
  const sid = String(sessionId ?? "").trim();
  const empty = {
    locked: false,
    affectedStatements: [],
    affectedItems: [],
    blockingStatuses: [],
  };
  if (!sid) return empty;

  const statements = Array.isArray(billingStatements) ? billingStatements : [];
  const items = Array.isArray(billingStatementItems)
    ? billingStatementItems
    : [];
  const stmtById = new Map(
    statements.map((s) => [String(s?.id ?? "").trim(), s]),
  );
  const BLOCK = new Set(
    Array.isArray(blockingStatuses) && blockingStatuses.length
      ? blockingStatuses
      : BILLING_LOCK_BLOCKING_STATEMENT_STATUSES,
  );

  const affectedItems = [];
  const blockingStatusSet = new Set();
  const stmtIds = new Set();

  for (const it of items) {
    if (String(it?.session_id ?? "").trim() !== sid) continue;
    const stId = String(it?.statement_id ?? "").trim();
    if (!stId) continue;
    const st = stmtById.get(stId);
    if (!st) continue;
    const status = String(st?.status ?? "").trim();
    if (!BLOCK.has(status)) continue;
    affectedItems.push(it);
    stmtIds.add(stId);
    blockingStatusSet.add(status);
  }

  const affectedStatements = statements.filter((s) =>
    stmtIds.has(String(s?.id ?? "").trim()),
  );

  return {
    locked: affectedItems.length > 0,
    affectedStatements,
    affectedItems,
    blockingStatuses: [...blockingStatusSet],
  };
}

/**
 * Bestätigung zurücksetzen: nur sperren, wenn zu dieser Session bereits verschickte/bezahlte
 * Rechnungen existieren (Entwürfe — auch bei anderen Teilnehmern im Gruppentraining — erlauben Reset).
 *
 * @param {string} sessionId
 * @param {{
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 *   billingCorrectionPostings?: unknown[],
 *   billingCorrectionsActiveHeaders?: unknown[],
 * }} ctx
 */
export function getSessionAttendanceResetPolicy(sessionId, ctx) {
  const sid = String(sessionId ?? "").trim();
  const emptyOk = {
    canReset: true,
    reasonKey: "",
    blockingStatementIds: [],
    blockingCorrectionIds: [],
  };
  if (!sid) return emptyOk;

  const safe = ctx && typeof ctx === "object" ? ctx : {};
  const corr = correctionPipelineBlocksSession(sid, safe);
  if (corr.blocked) {
    return {
      canReset: false,
      reasonKey: corr.reasonKey,
      blockingStatementIds: [],
      blockingCorrectionIds: corr.blockingCorrectionIds || [],
    };
  }

  const lock = getBillingLockInfoForSession({
    sessionId: sid,
    billingStatements: safe.billingStatements,
    billingStatementItems: safe.billingStatementItems,
    blockingStatuses: BILLING_SENT_STATEMENT_STATUSES,
  });

  if (lock.locked) {
    return {
      canReset: false,
      reasonKey: "trainerstunden.billingCorrection.resetBlockedHint",
      blockingStatementIds: (lock.affectedStatements || [])
        .map((s) => String(s?.id ?? "").trim())
        .filter(Boolean),
      blockingCorrectionIds: [],
    };
  }

  return emptyOk;
}

/**
 * Anwesenheit bestätigen: Erstbestätigung auch bei verschickter Rechnung erlauben
 * (Nachziehen nach Monatsabschluss). Korrektur/Reset bleibt gesperrt.
 *
 * @param {string} sessionId
 * @param {{ attendance_confirmed?: boolean } | null | undefined} session
 * @param {Record<string, unknown>|null|undefined} updates
 * @param {{
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 *   billingCorrectionPostings?: unknown[],
 *   billingCorrectionsActiveHeaders?: unknown[],
 * }} ctx
 */
export function getSessionAttendanceConfirmPolicy(
  sessionId,
  session,
  updates,
  ctx,
) {
  const sid = String(sessionId ?? "").trim();
  const sess = session && typeof session === "object" ? session : null;
  const u = updates && typeof updates === "object" ? updates : {};
  const wasConfirmed = Boolean(sess?.attendance_confirmed);
  const isFirstConfirm = u.attendance_confirmed === true && !wasConfirmed;

  if (isFirstConfirm) {
    const corr = correctionPipelineBlocksSession(sid, ctx);
    if (corr.blocked) {
      return {
        canConfirm: false,
        reasonKey: corr.reasonKey,
        blockingStatementIds: [],
        blockingCorrectionIds: corr.blockingCorrectionIds || [],
      };
    }
    return {
      canConfirm: true,
      reasonKey: "",
      blockingStatementIds: [],
      blockingCorrectionIds: [],
    };
  }

  const resetPolicy = getSessionAttendanceResetPolicy(sid, ctx);
  return {
    canConfirm: resetPolicy.canReset,
    reasonKey: resetPolicy.reasonKey,
    blockingStatementIds: resetPolicy.blockingStatementIds || [],
    blockingCorrectionIds: resetPolicy.blockingCorrectionIds || [],
  };
}

/**
 * Darf eine Ursprungs-Session/Leistung noch direkt bearbeitet werden (Planung),
 * unabhängig vom aktuell angezeigten Rechnungsempfänger?
 * Prüft alle Statement-Positionen derselben Session, gebuchte Korrektur-Postings und aktive Korrekturköpfe.
 *
 * @param {string} sessionId
 * @param {{
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 *   billingCorrectionPostings?: unknown[],
 *   billingCorrectionsActiveHeaders?: unknown[],
 * }} ctx
 * @returns {{
 *   canEdit: boolean,
 *   reasonKey: string,
 *   blockingStatementIds: string[],
 *   blockingCorrectionIds: string[],
 *   blockingRecipientIds: string[],
 * }}
 */
export function getSourceSessionPerformanceEditPolicy(sessionId, ctx) {
  const sid = String(sessionId ?? "").trim();
  const emptyOk = {
    canEdit: true,
    reasonKey: "",
    blockingStatementIds: [],
    blockingCorrectionIds: [],
    blockingRecipientIds: [],
  };
  if (!sid) return emptyOk;

  const safe = ctx && typeof ctx === "object" ? ctx : {};
  const statements = Array.isArray(safe.billingStatements)
    ? safe.billingStatements
    : [];
  const items = Array.isArray(safe.billingStatementItems)
    ? safe.billingStatementItems
    : [];
  const corr = correctionPipelineBlocksSession(sid, safe);
  if (corr.blocked) {
    return {
      canEdit: false,
      reasonKey: corr.reasonKey,
      blockingStatementIds: [],
      blockingCorrectionIds: corr.blockingCorrectionIds || [],
      blockingRecipientIds: [],
    };
  }

  const lock = getBillingLockInfoForSession({
    sessionId: sid,
    billingStatements: statements,
    billingStatementItems: items,
  });

  if (lock.locked) {
    const blockingStatementIds = (lock.affectedStatements || [])
      .map((s) => String(s?.id ?? "").trim())
      .filter(Boolean);
    const blockingRecipientIds = [];
    for (const st of lock.affectedStatements || []) {
      const rt = String(st?.recipient_type ?? "").trim();
      const pid = String(st?.person_id ?? "").trim();
      const bcid = String(st?.billing_contact_id ?? "").trim();
      if (rt === "contact" && bcid)
        blockingRecipientIds.push(`contact:${bcid}`);
      else if (pid) blockingRecipientIds.push(`person:${pid}`);
    }
    return {
      canEdit: false,
      reasonKey:
        "trainerstunden.billing.openItem.performanceBlockedMaterialized",
      blockingStatementIds: [...new Set(blockingStatementIds)],
      blockingCorrectionIds: [],
      blockingRecipientIds: [...new Set(blockingRecipientIds)],
    };
  }

  return emptyOk;
}

/**
 * Darf zu einer offenen Leistungsposition eine Korrektur erfasst werden?
 * Blockiert bei verschickter Rechnung (gleicher oder anderer Teilnehmer) oder aktiver Korrektur-Pipeline.
 *
 * @param {string} sessionId
 * @param {string} linePersonId
 * @param {{
 *   billingStatements?: unknown[],
 *   billingStatementItems?: unknown[],
 *   billingCorrectionPostings?: unknown[],
 *   billingCorrectionsActiveHeaders?: unknown[],
 * }} ctx
 * @returns {{
 *   canRecordCorrection: boolean,
 *   reasonKey: string,
 *   blockingRecipientIds: string[],
 *   blockingCorrectionIds: string[],
 * }}
 */
export function getOpenLineItemCorrectionPolicy(sessionId, linePersonId, ctx) {
  const sid = String(sessionId ?? "").trim();
  const linePid = normalizePid(linePersonId);
  const emptyOk = {
    canRecordCorrection: true,
    reasonKey: "",
    blockingRecipientIds: [],
    blockingCorrectionIds: [],
  };
  if (!sid) return emptyOk;

  const safe = ctx && typeof ctx === "object" ? ctx : {};
  const statements = Array.isArray(safe.billingStatements)
    ? safe.billingStatements
    : [];
  const items = Array.isArray(safe.billingStatementItems)
    ? safe.billingStatementItems
    : [];
  const postings = Array.isArray(safe.billingCorrectionPostings)
    ? safe.billingCorrectionPostings
    : [];
  const corrHdr = Array.isArray(safe.billingCorrectionsActiveHeaders)
    ? safe.billingCorrectionsActiveHeaders
    : [];
  const stmtById = new Map(
    statements.map((s) => [String(s?.id ?? "").trim(), s]),
  );

  const blockingCorrFromPostings = new Set();
  for (const p of postings) {
    if (String(p?.correction_session_id ?? "").trim() !== sid) continue;
    if (String(p?.correction_status ?? "").trim() !== "booked") continue;
    const cid = String(p?.correction_id ?? "").trim();
    if (cid) blockingCorrFromPostings.add(cid);
  }
  if (blockingCorrFromPostings.size > 0) {
    return {
      canRecordCorrection: false,
      reasonKey:
        "trainerstunden.billing.openItem.performanceBlockedCorrectionBooked",
      blockingRecipientIds: [],
      blockingCorrectionIds: [...blockingCorrFromPostings],
    };
  }

  const activeHdr = corrHdr.filter((c) => {
    if (String(c?.session_id ?? "").trim() !== sid) return false;
    const st = String(c?.status ?? "").trim();
    return st === "draft" || st === "confirmed" || st === "booked";
  });
  if (activeHdr.length > 0) {
    return {
      canRecordCorrection: false,
      reasonKey:
        "trainerstunden.billing.openItem.performanceBlockedCorrectionPipeline",
      blockingRecipientIds: [],
      blockingCorrectionIds: [
        ...new Set(
          activeHdr.map((c) => String(c?.id ?? "").trim()).filter(Boolean),
        ),
      ],
    };
  }

  const blockingRecipientIds = [];
  for (const it of items) {
    if (String(it?.session_id ?? "").trim() !== sid) continue;
    const stId = String(it?.statement_id ?? "").trim();
    if (!stId) continue;
    const st = stmtById.get(stId);
    if (!st) continue;
    const status = String(st?.status ?? "").trim();
    if (!SENT.has(status)) continue;

    const itemPid = normalizePid(it?.person_id);
    if (itemPid === linePid) {
      return {
        canRecordCorrection: false,
        reasonKey:
          "trainerstunden.billing.openItem.correctionBlockedSentInvoice",
        blockingRecipientIds: [],
        blockingCorrectionIds: [],
      };
    }

    const rt = String(st?.recipient_type ?? "").trim();
    const pid = String(st?.person_id ?? "").trim();
    const bcid = String(st?.billing_contact_id ?? "").trim();
    if (rt === "contact" && bcid) blockingRecipientIds.push(`contact:${bcid}`);
    else if (pid) blockingRecipientIds.push(`person:${pid}`);
  }

  if (blockingRecipientIds.length > 0) {
    return {
      canRecordCorrection: false,
      reasonKey:
        "trainerstunden.billing.openItem.correctionBlockedOtherPlayerSent",
      blockingRecipientIds: [...new Set(blockingRecipientIds)],
      blockingCorrectionIds: [],
    };
  }

  return emptyOk;
}
